import React from "react";
import FilterBar from "@/components/common/FilterBar";
import StickyFilterBar from "@/components/common/StickyFilterBar";
import { calculateDateRange, DATE_RANGE_KEYS, getRecalculatedDatesIfNeeded } from "@/utils/dateRangeUtils";
import { DateRangeChange, FilterChangeEvent } from "@/types";
import { useDashboardData } from "@/contexts/DashboardContext";
import { useFilterCache } from "@/hooks/useFilterCache";
import OutboundCallStatusWidget from "@/components/widgets/call-overview/OutboundCallStatusWidget";
import InboundDirectCallsDistributionWidget from "@/components/widgets/call-overview/InboundDirectCallsDistributionWidget";
import CallVolumeDistributionWidget from "@/components/widgets/call-overview/CallVolumeDistributionWidget";
import DirectCallJourneyTableWidget from "@/components/widgets/direct-call/DirectCallJourneyTableWidget";
import { fetchDirectWidgetsBatch, DirectWidgetsBatchResult } from "@/lib/axios/api/widgets.api";
import { SectionType } from "@/constants/section";
import { CallQueueType, EMPTY_AGENT_ID, EMPTY_QUEUE_ID } from "@/constants/queue";
import { useAppData, UserRole } from "@/contexts/AppDataContext";

// "Inbound" (queue-routed) is intentionally excluded from the Direct Call tab's
// Call Volume chart — this tab only covers direct/outbound traffic.
function buildCallVolumeRows(startDateIso: string, callVolume: DirectWidgetsBatchResult["callVolume"]) {
  const start = new Date(startDateIso);
  const getSeries = (key: string) => callVolume.series.find(s => s.key === key)?.values ?? [];
  const outbound = getSeries("Outbound");
  const inboundDirect = getSeries("Inbound Direct");
  const internal = getSeries("Internal");
  return callVolume.labels.map((_, i) => ({
    time: new Date(start.getTime() + i * callVolume.intervalMinutes * 60000),
    outboundCall: outbound[i] ?? 0,
    inboundDirectCall: inboundDirect[i] ?? 0,
    internalCall: internal[i] ?? 0,
  }));
}

const DirectCallTab: React.FC<{ isGeneralMode?: boolean; filterClass?: string }> = ({ isGeneralMode, filterClass }) => {
  const defaultDateRangeKey = DATE_RANGE_KEYS.LAST_7_DAYS;
  const { current } = useDashboardData();
  const { filters, saveFilters } = useFilterCache("direct-call-dashboard", current?.id);
  const { userRole, uniqueAgents, queueMicrosoftIds, queuesDataVersion, directAgentGroups } = useAppData();

  // TCX-6503: role scope and manual filter are two different things and travel separately.
  // Role scope narrows which calls exist; the manual filter narrows within them and is the only
  // thing that switches a widget's calculation rule. Mirrors KeyMetricsTab's role-scope memo.
  //
  // This replaces TCX-6490's approach of injecting the role scope into the manual filter, which
  // made a Manager who picked nothing indistinguishable from one who selected their whole scope,
  // and let an empty scope read as "no restriction" (tenant-wide data).
  const roleScope = React.useMemo(() => {
    if (userRole !== UserRole.MANAGER) {
      return { queueIds: [] as string[], agentIds: [] as string[], directAgentIds: [] as string[] };
    }
    return {
      queueIds: queueMicrosoftIds.length > 0 ? queueMicrosoftIds : [EMPTY_QUEUE_ID],
      agentIds: uniqueAgents.length > 0 ? uniqueAgents.map(agent => agent.microsoftId) : [EMPTY_AGENT_ID],
      directAgentIds: [] as string[],
    };
  }, [userRole, uniqueAgents, queueMicrosoftIds]);

  // Manual filter only — deliberately no role fallback. Memoized because DirectCallJourneyTableWidget's
  // fetch effect depends on it by reference; without this it aborts and re-fires its
  // /api/direct/table request on every parent render, not just on real changes.
  //
  // TCX-fix-role: when the manual filter is a Direct Agent Group, re-expand it against *live*
  // group membership (directAgentGroups from AppDataContext, refreshed by the 5s role/queue poll)
  // instead of trusting `filters.agents` — that field is a frozen agent-id snapshot taken by
  // FilterBar.handleDirectCallAgentChange at the moment the group was picked, so an agent
  // added/removed from the group afterwards would otherwise only take effect once the user
  // manually re-picks the filter. Mirrors Queue tab's `resolveScopedIds` (TCX-6502).
  const manualAgentIds = React.useMemo(() => {
    if (filters?.selectedQueueType === CallQueueType.DIRECT_AGENT_GROUP) {
      const selectedGroupIds = filters?.queues ?? [];
      if (selectedGroupIds.length === 0) return filters?.agents ?? [];
      const expanded = Array.from(new Set(
        (directAgentGroups ?? [])
          .filter(group => selectedGroupIds.includes(group.microsoftId))
          .flatMap(group => group.agentMicrosoftIds)
      ));
      return expanded.length === 0 ? [EMPTY_AGENT_ID] : expanded;
    }
    return filters?.agents ?? [];
  }, [filters?.selectedQueueType, filters?.queues, filters?.agents, directAgentGroups]);
  const [dateRangeKey, setDateRangeKey] = React.useState<string>(filters?.dateRangeKey ?? defaultDateRangeKey);
  const [dateRange, setDateRange] = React.useState<DateRangeChange>(() => {
    if (filters) {
      const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(
        filters.dateRangeKey,
        filters
      );
      return { key: filters.dateRangeKey, startDate, endDate, previousStartDate, previousEndDate };
    }
    return {
      key: defaultDateRangeKey,
      startDate: null,
      endDate: null,
      previousStartDate: null,
      previousEndDate: null,
    };
  });

  React.useEffect(() => {
    if (!filters) {
      const dr = calculateDateRange(defaultDateRangeKey);
      setDateRange({
        key: defaultDateRangeKey,
        startDate: dr.from.toISOString(),
        endDate: dr.to.toISOString(),
        previousStartDate: dr.previousFrom.toISOString(),
        previousEndDate: dr.previousTo.toISOString(),
      });
      setDateRangeKey(defaultDateRangeKey);
    }
  }, [filters]);

  // Refresh data when tab becomes active — recalc dynamic ranges (e.g. "Today so far") to current time
  React.useEffect(() => {
    if (isGeneralMode) {
      setDateRange(prev => {
        if (!prev.startDate || !prev.endDate || !prev.previousStartDate || !prev.previousEndDate) return prev;
        const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(prev.key, {
          startDate: prev.startDate,
          endDate: prev.endDate,
          previousStartDate: prev.previousStartDate,
          previousEndDate: prev.previousEndDate,
        });
        return { ...prev, startDate, endDate, previousStartDate, previousEndDate };
      });
    }
  }, [isGeneralMode]);

  const [widgetsData, setWidgetsData] = React.useState<DirectWidgetsBatchResult | null>(null);
  const [widgetsLoading, setWidgetsLoading] = React.useState(false);
  const [widgetsError, setWidgetsError] = React.useState(false);

  React.useEffect(() => {
    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate) return;
    const controller = new AbortController();
    setWidgetsLoading(true);
    setWidgetsError(false);
    fetchDirectWidgetsBatch(
      { startDate, endDate, roleScope, manualAgentIds },
      { signal: controller.signal }
    )
      .then(result => setWidgetsData(result))
      .catch(err => {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setWidgetsError(true);
      })
      .finally(() => setWidgetsLoading(false));
    return () => controller.abort();
  }, [dateRange.startDate, dateRange.endDate, roleScope, manualAgentIds, queuesDataVersion]);

  const handleFilterChange = (event: FilterChangeEvent) => {
    setDateRangeKey(event.dateRange.key);

    const rawDateRange = {
      dateRangeKey: event.dateRange.key,
      startDate: event.dateRange.startDate.toISOString(),
      endDate: event.dateRange.endDate.toISOString(),
      previousStartDate: event.dateRange.previousStartDate.toISOString(),
      previousEndDate: event.dateRange.previousEndDate.toISOString(),
    };

    const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(
      event.dateRange.key,
      rawDateRange
    );

    setDateRange({
      key: event.dateRange.key,
      startDate,
      endDate,
      previousStartDate,
      previousEndDate,
    });

    saveFilters({
      ...rawDateRange,
      queues: event.queues ?? [],
      agents: event.agents ?? [],
      selectedQueueType: event.selectedQueueType,
      searchTerm: "",
    });
  };

  return (
    <div className="w-full">
      <div className="border text-primary bg-[#FCFCFD] rounded-lg dark:border-[#3C5C73] dark:bg-[#3C5C73] dark:text-white px-[8px] pb-[8px] pt-1">
        <StickyFilterBar className={filterClass}>
          <FilterBar
            onFilterChange={handleFilterChange}
            initialDateRangeKey={dateRangeKey}
            initialSelectedAgents={filters?.agents}
            initialSelectedQueues={filters?.queues}
            initialSelectedQueueType={filters?.selectedQueueType ?? CallQueueType.DIRECT_AGENT_GROUP}
            initialDateRange={dateRange}
            sectionType={SectionType.DIRECT_CALL}
          />
        </StickyFilterBar>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row gap-4 items-stretch">
            <div className="flex flex-col gap-4 w-full lg:w-1/3">
              <OutboundCallStatusWidget
                loading={widgetsLoading}
                error={widgetsError ? "Failed to load outbound call status" : undefined}
                data={{
                  answeredCall: widgetsData?.outboundStatus.answeredCall ?? 0,
                  missedCall: widgetsData?.outboundStatus.missedCall ?? 0,
                }}
              />
              <InboundDirectCallsDistributionWidget
                loading={widgetsLoading}
                error={widgetsError ? "Failed to load inbound direct call status" : undefined}
                data={{
                  answeredCall: widgetsData?.inboundDirectStatus.answeredCall ?? 0,
                  missedCall: widgetsData?.inboundDirectStatus.missedCall ?? 0,
                }}
              />
            </div>
            <div className="w-full lg:w-2/3">
              <CallVolumeDistributionWidget
                loading={widgetsLoading}
                error={widgetsError ? "Failed to load call volume" : undefined}
                dateRangeType={dateRangeKey}
                dateRange={{
                  from: dateRange.startDate ? new Date(dateRange.startDate) : new Date(),
                  to: dateRange.endDate ? new Date(dateRange.endDate) : new Date(),
                }}
                data={{
                  callOverviewData: {
                    callVolumeDistribution: widgetsData?.callVolume && dateRange.startDate
                      ? buildCallVolumeRows(dateRange.startDate, widgetsData.callVolume)
                      : [],
                  },
                }}
              />
            </div>
          </div>
          <div className="h-[500px]">
            <DirectCallJourneyTableWidget
              startDate={dateRange.startDate ?? undefined}
              endDate={dateRange.endDate ?? undefined}
              roleScope={roleScope}
              manualAgentIds={manualAgentIds}
              queuesDataVersion={queuesDataVersion}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DirectCallTab;
